// src/contexts/OperatorContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import { pb } from "../services/pocketbase";
import { useAuth } from "./AuthContext";
import { 
  getActiveOperator, 
  setActiveOperator, 
  clearActiveOperator, 
  hashPin 
} from "../utils/operatorUtils";

const OperatorContext = createContext({});

export function OperatorProvider({ children }) {
  const { user } = useAuth();
  const [activeOperator, setActiveOperatorState] = useState(null);
  const [showSelectModal, setShowSelectModal] = useState(false);

  // Determina se é terminal compartilhado (Oficina ou PDV)
  const SHARED_TERMINALS = ["OFICINA", "PDV1"];
  const isSharedTerminal = user && SHARED_TERMINALS.some((term) =>
    [user.username, user.name].some((val) => val && val.toUpperCase().includes(term))
  );

  // Determina o tipo de terminal ('PDV' ou 'OFICINA')
  const getTerminalType = () => {
    if (!user) return "OFICINA";
    const nameUpper = String(user.name || "").toUpperCase();
    const userUpper = String(user.username || "").toUpperCase();
    if (nameUpper.includes("PDV") || userUpper.includes("PDV")) {
      return "PDV";
    }
    return "OFICINA";
  };
  const terminalType = getTerminalType();

  // Carrega o operador do localStorage quando o usuário estiver pronto
  useEffect(() => {
    if (user && isSharedTerminal) {
      const stored = getActiveOperator(terminalType);
      setActiveOperatorState(stored);
    } else {
      setActiveOperatorState(null);
    }
  }, [user, isSharedTerminal, terminalType]);

  // Define o tempo baseado no operador ativo (ou fallback para 5 minutos)
  const timeoutMinutes = activeOperator && typeof activeOperator.timeout_minutos === "number"
    ? activeOperator.timeout_minutos
    : 5;
  const timeoutSeconds = timeoutMinutes * 60;
  
  const [timeLeft, setTimeLeft] = useState(timeoutSeconds);

  // Sincroniza o timeLeft se a configuração de tempo de inatividade mudar (login/troca de operador)
  useEffect(() => {
    setTimeLeft(timeoutSeconds);
  }, [timeoutSeconds]);

  // 1. Efeito do Cronômetro de Contagem Regressiva
  useEffect(() => {
    if (!isSharedTerminal || !activeOperator) {
      return;
    }

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          logoutOperator();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isSharedTerminal, activeOperator, timeoutSeconds]);

  // 2. Ouvintes de Atividade Humana Intencional (sem mousemove ou scroll para evitar falsos positivos)
  useEffect(() => {
    if (!isSharedTerminal || !activeOperator) {
      return;
    }

    const resetTimer = () => {
      setTimeLeft(timeoutSeconds);
    };

    // Registrar eventos intencionais
    window.addEventListener("mousedown", resetTimer);
    window.addEventListener("keydown", resetTimer);
    window.addEventListener("touchstart", resetTimer);

    return () => {
      window.removeEventListener("mousedown", resetTimer);
      window.removeEventListener("keydown", resetTimer);
      window.removeEventListener("touchstart", resetTimer);
    };
  }, [isSharedTerminal, activeOperator, timeoutSeconds]);

  // Se for terminal compartilhado e não houver operador ativo, força a abertura do modal
  useEffect(() => {
    if (isSharedTerminal && !activeOperator) {
      setShowSelectModal(true);
    } else {
      setShowSelectModal(false);
    }
  }, [isSharedTerminal, activeOperator]);

  // Login de operador
  const loginOperator = async (operator, pin) => {
    try {
      const hashed = await hashPin(pin);
      if (operator.pin === hashed) {
        setActiveOperator(operator, terminalType);
        setActiveOperatorState(operator);
        // Atualiza imediatamente o tempo restante para o novo operador
        const mins = typeof operator.timeout_minutos === "number" ? operator.timeout_minutos : 5;
        setTimeLeft(mins * 60);
        setShowSelectModal(false);
        return true;
      }
      return false;
    } catch (e) {
      console.error("Erro ao validar PIN do operador:", e);
      return false;
    }
  };

  // Logout de operador
  const logoutOperator = () => {
    clearActiveOperator(terminalType);
    setActiveOperatorState(null);
    setTimeLeft(300); // Reseta para default 5 minutos
    if (isSharedTerminal) {
      setShowSelectModal(true);
    }
  };

  return (
    <OperatorContext.Provider
      value={{
        activeOperator,
        isSharedTerminal,
        terminalType,
        showSelectModal,
        setShowSelectModal,
        loginOperator,
        logoutOperator,
        timeLeft,
      }}
    >
      {children}
    </OperatorContext.Provider>
  );
}

export function useOperator() {
  return useContext(OperatorContext);
}
